import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Building2, ExternalLink, AlertTriangle } from 'lucide-react';
import { getEventsCalendar } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO } from 'date-fns';

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [locationFilter, setLocationFilter] = useState('');

  useEffect(() => {
    document.title = 'Calendar | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [currentDate, locationFilter]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEventsCalendar({
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        location: locationFilter || undefined,
      });
      setCalendarData(response.data.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load calendar'));
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad the beginning of the month
  const startDay = monthStart.getDay();
  const paddedDays = Array(startDay).fill(null).concat(days);

  const getEventsForDate = (date) => {
    if (!calendarData?.eventsByDate || !date) return [];
    const dateKey = format(date, 'yyyy-MM-dd');
    return calendarData.eventsByDate[dateKey] || [];
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title mb-1">Event Calendar</h1>
        <p className="text-sm text-ink-500">View competitor events by date to plan your schedule</p>
      </div>

      {error && (
        <div className="card bg-red-50 border border-red-200">
          <p className="text-red-700">{error}</p>
          <button onClick={() => { setError(null); loadCalendar(); }} className="btn-secondary mt-2">
            Try again
          </button>
        </div>
      )}

      {/* Location Filter */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="calendar-location-filter" className="block text-sm font-medium text-gray-700 mb-1">Filter by Location</label>
            <input
              id="calendar-location-filter"
              type="text"
              className="input"
              placeholder="e.g., Liverpool, Manchester..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-500">
            <span className="font-semibold text-ink-700">{calendarData?.totalEvents || 0} events this month</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-dental-100 ring-1 ring-dental-200" /> Event</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-100 ring-1 ring-purple-200" /> Multi-day</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 ring-1 ring-red-200" /> Conflict</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 card">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dental-600"></div>
            </div>
          ) : (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs sm:text-sm font-medium text-gray-500 py-2">
                    {/* Single letter on mobile so 7 columns fit narrow screens */}
                    <span className="sm:hidden">{day.charAt(0)}</span>
                    <span className="hidden sm:inline">{day}</span>
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {paddedDays.map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} className="min-h-[4rem] h-20 sm:h-24 bg-gray-50 rounded-lg" />;
                  }

                  const events = getEventsForDate(day);
                  const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                  const hasConflict = events.length > 1;

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => setSelectedDate(day)}
                      className={`min-h-[4rem] h-20 sm:h-24 p-2 rounded-xl ring-1 transition-all text-left ${
                        isSelected
                          ? 'ring-2 ring-dental-500 bg-dental-50'
                          : hasConflict
                            ? 'ring-red-200 bg-red-50/40 hover:ring-red-300'
                            : 'ring-ink-100 hover:ring-ink-300 bg-white'
                      }`}
                    >
                      <div className={`text-sm font-semibold inline-flex items-center justify-center ${
                        isToday(day) ? 'bg-dental-600 text-white w-6 h-6 rounded-full' : 'text-ink-900'
                      }`}>
                        {format(day, 'd')}
                      </div>
                      {events.length > 0 && (
                        <div className="mt-1 space-y-1 max-sm:line-clamp-3">
                          {events.slice(0, 2).map((event, i) => (
                            <div
                              key={i}
                              className={`text-xs truncate px-1 py-0.5 rounded ${
                                hasConflict 
                                  ? 'bg-red-100 text-red-700' 
                                  : event.isMultiDay 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-dental-100 text-dental-700'
                              }`}
                              title={event.isMultiDay ? `${event.startDate} - ${event.endDate}` : undefined}
                            >
                              {event.isMultiDay && !event.isFirstDay && '↳ '}
                              {event.clinicName || event.eventTitle}
                            </div>
                          ))}
                          {events.length > 2 && (
                            <div className="text-xs text-gray-500">
                              +{events.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Selected Date Details */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">
            {selectedDate
              ? format(selectedDate, 'EEEE, MMMM d, yyyy')
              : 'Select a date'}
          </h3>

          {selectedDate && selectedEvents.length === 0 && (
            <p className="text-gray-500 text-sm">No events on this date.</p>
          )}

          {selectedEvents.length > 1 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm font-medium">
                ⚠️ {selectedEvents.length} events on this date - potential conflict!
              </p>
            </div>
          )}

          <div className="space-y-4">
            {selectedEvents.map((event, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">{event.eventTitle || 'Untitled Event'}</h4>
                
                {event.isMultiDay && (
                  <div className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded inline-block mb-2">
                    📅 Multi-day: {event.startDate} - {event.endDate}
                  </div>
                )}
                
                {event.clinicName && (
                  <div className="flex items-center gap-2 text-sm text-ink-600 mb-1">
                    <Building2 className="w-4 h-4 shrink-0" />
                    <Link to={`/clinics/${encodeURIComponent(event.clinicName)}`} className="hover:text-dental-600 hover:underline truncate">
                      {event.clinicName}
                    </Link>
                  </div>
                )}

                {event.location && (
                  <div className="flex items-center gap-2 text-sm text-ink-600 mb-1">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <Link to={`/locations/${encodeURIComponent(event.location)}`} className="hover:text-dental-600 hover:underline truncate">
                      {event.location}
                    </Link>
                  </div>
                )}

                {event.offer && (
                  <span className="badge badge-success mt-2">{event.offer}</span>
                )}

                {event.treatmentTypes && event.treatmentTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {event.treatmentTypes.map(t => (
                      <span key={t} className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}

                {event.adUrl && (
                  <a
                    href={event.adUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-sm text-dental-600 hover:text-dental-700 font-medium"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Real Ad
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Calendar;
